import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { AppLayout } from '@/components/common/AppLayout';
import { AuthGuard } from '@/components/common/AuthGuard';
import { RoleHome } from '@/components/common/RoleHome';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { RouteError } from '@/components/common/RouteError';

// Public portal (respondents, no login)
const Home = lazy(() => import('@/pages/public/Home'));
const RequestWizard = lazy(() => import('@/pages/public/RequestWizard'));
const RequestSuccess = lazy(() => import('@/pages/public/RequestSuccess'));
const Track = lazy(() => import('@/pages/public/Track'));
const Privacy = lazy(() => import('@/pages/public/Privacy'));
// Staff
const Login = lazy(() => import('@/pages/auth/Login'));
const SsoCallback = lazy(() => import('@/pages/auth/SsoCallback'));
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Requests = lazy(() => import('@/pages/admin/Requests'));
const RequestDetail = lazy(() => import('@/pages/admin/RequestDetail'));
const InternalRequest = lazy(() => import('@/pages/admin/InternalRequest'));
const Events = lazy(() => import('@/pages/admin/Events'));
const EventForm = lazy(() => import('@/pages/admin/EventForm'));
const EntryHome = lazy(() => import('@/pages/admin/EntryHome'));
const Users = lazy(() => import('@/pages/admin/Users'));

const wrap = (element: React.ReactNode) => <Suspense fallback={<LoadingSpinner fullScreen />}>{element}</Suspense>;

export const router = createBrowserRouter([
  // ── Public portal ─────────────────────────────────────────────────────────
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: wrap(<Home />) },
      { path: 'nueva', element: wrap(<RequestWizard />) },
      { path: 'gracias/:code', element: wrap(<RequestSuccess />) },
      { path: 'seguimiento', element: wrap(<Track />) },
      { path: 'seguimiento/:code', element: wrap(<Track />) },
      { path: 'privacidad', element: wrap(<Privacy />) },
    ],
  },

  // ── Stable short links (QR codes / posters). They survive any internal route change;
  //    unauthenticated visitors are sent to login and brought back by AuthGuard. ──
  { path: '/registro-evento', element: <Navigate to="/admin/eventos/nuevo" replace /> },
  { path: '/registro-interno', element: <Navigate to="/admin/solicitudes/nueva" replace /> },

  // ── Auth ──────────────────────────────────────────────────────────────────
  { path: '/login', element: wrap(<Login />), errorElement: <RouteError /> },
  { path: '/sso-callback', element: wrap(<SsoCallback />), errorElement: <RouteError /> },

  // ── Staff back-office (JWT-protected) ─────────────────────────────────────
  {
    path: '/admin',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <RoleHome /> },
      // Restricted EVENT_REQUEST role: entry page + the two forms (eventos/:id = its own new event, 24 h, enforced by the API)
      { path: 'inicio', element: wrap(<EntryHome />) },
      { path: 'solicitudes/nueva', element: wrap(<InternalRequest />) },
      { path: 'eventos/nuevo', element: wrap(<EventForm />) },
      { path: 'eventos/:id', element: wrap(<EventForm />) },
      // Everything else needs AGENT (ADMIN implicit)
      { path: 'dashboard', element: <AuthGuard roles={['AGENT']}>{wrap(<Dashboard />)}</AuthGuard> },
      { path: 'solicitudes', element: <AuthGuard roles={['AGENT']}>{wrap(<Requests />)}</AuthGuard> },
      { path: 'solicitudes/:id', element: <AuthGuard roles={['AGENT']}>{wrap(<RequestDetail />)}</AuthGuard> },
      { path: 'eventos', element: <AuthGuard roles={['AGENT']}>{wrap(<Events />)}</AuthGuard> },
      { path: 'usuarios', element: <AuthGuard roles={['ADMIN']}>{wrap(<Users />)}</AuthGuard> },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
]);
